/* eslint-disable react/prop-types */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import useGetReturnItems from '../../../Hooks/returns/useGetReturnItems';
import useGetReturn from '../../../Hooks/returns/useGetReturn';
import useGetReturnItemsXls from '../../../Hooks/returns/useGetReturnItemsXls';
import useGetReturnItemsPdf from '../../../Hooks/returns/useGetReturnItemsPdf';
import usePutReturnStatus from '../../../Hooks/returns/usePutReturnStatus';
import usePutIsseenOrder from '../../../Hooks/orders/usePutIsseenOrder';
import ImageBox from '../../ImageBox';
import PdfIconButton from '../../IconButtons/PdfIconButton';
import ExcelIconButton from '../../IconButtons/ExcelIconButton';
import ReturnsItemsTable from '../../Tables/ReturnsItemsTable';
import CommentModal from '../Orders/CommentModal';
import OrderDetailsSkeleton from '../../Skeletons/OrderDetailsSkeleton';

const CustomModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  backgroundColor: 'transparent',
  borderRadius: 6,
  boxShadow: 24,
  padding: '35px 25px',
  maxHeight: '90vh',
  overflow: 'auto',
  '&::-webkit-scrollbar': {
    width: '4px',
    height: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,.1)',
  },
  [theme.breakpoints.up('md')]: {
    width: '90%',
  },
}));

const CustomLeftGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderRadius: 6,
  boxShadow: 24,
  padding: '35px 25px',
  width: '78%',
  marginRight: '15px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

const CustomRightGrid = styled(Grid)(({ theme }) => ({
  background: 'linear-gradient(90deg, rgba(245,110,76,1) 0%, rgba(241,135,48,1) 100%)',
  borderRadius: 6,
  boxShadow: 24,
  padding: '50px 25px',
  width: '20%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: '30px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const CustomGridContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
  },
}));

const CustomContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& .MuiTypography-root': {
    textAlign: 'center',
  },
  '& .return-info': {
    margin: '15px',
  },
  '& .buttons-container': {
    paddingTop: '160px',
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiTypography-root': {
      textAlign: 'left',
    },
    '& .return-info': {
      margin: 0,
    },
    '& .buttons-container': {
      paddingTop: '20px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiTypography-root': {
      textAlign: 'center',
    },
  },
}));

const CustomTypography = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: 14,
  letterSpacing: 1.5,
  textAlign: 'center',
}));

const CustomIconBox = styled(Box)(() => ({
  backgroundColor: '#fff',
  width: 35,
  height: 35,
  padding: 0,
  borderRadius: '50%',
  boxShadow: '-7px 3px 15px -6px #302f2f',
  border: '1px solid #EAEAEA',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

function CreateData(code, name, quantity, id) {
  return {
    code,
    name,
    quantity,
    id,
  };
}

function EditReturnModal({ ...props }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [page, setPage] = useState(1);
  const [param, setParam] = useState(`${props.orderId}/items?page=${page}`);
  const { mutate: putIsseenOrder } = usePutIsseenOrder();
  let count = 0;
  let limit = 10;

  const handleOpen = () => {
    setOpen(true);
    putIsseenOrder({ id: props.returnId });
  };
  const handleClose = () => {
    setOpen(false);
    props.refetchReturns(true);
  };

  useEffect(() => {
    setParam(`${props.returnId}/items?page=${page}`);
  }, [page]);

  const { t } = useTranslation();
  const {
    data,
    isFetched,
    status: itemsStatus,
    refetch,
  } = useGetReturnItems(param);
  const {
    data: dataReturn,
    isFetched: isFetchedReturn,
    status: getReturnStatus,
    refetch: refetchReturn,
  } = useGetReturn(props.returnId);
  const {
    refetch: refetchReturnItemsXls,
  } = useGetReturnItemsXls(props.returnId);
  const {
    refetch: refetchReturnItemsPdf,
  } = useGetReturnItemsPdf(props.returnId);
  const { mutate: putReturnStatus } = usePutReturnStatus();

  const rows = [];
  let returnEl = {};

  if (isFetched) {
    const returnItems = data && data?.data && data.data.slice(0, 10);
    const ordersNumber = data && data.headers['x-pagination'].split(', ')[0].match(/\d/g).join('');
    limit = data && data.headers['x-pagination'].split(', ')[1].match(/\d/g).join('');
    count = Math.ceil(+ordersNumber / +limit);
    returnItems?.map(item => rows.push(
      CreateData(item.item_code, item.name, item.amount, item.id),
    ));
  }

  if (isFetchedReturn && dataReturn && dataReturn?.data) {
    returnEl = dataReturn.data;
    if (!props.seen) {
      props.setIsSeenOrder(true);
    } else {
      props.setIsSeenOrder(false);
    }
  }

  const handleSubmit = (event, status) => {
    event.preventDefault();
    const returnStatus = status;
    const id = props.returnId;
    const notice = comment;
    return putReturnStatus(({ id, returnStatus, notice }), {
      onSuccess: async () => {
        if (returnStatus === 'accepted') {
          setIsConfirmed(true);
        } else if (returnStatus === 'rejected') {
          setIsRejected(true);
        }
      },
      onError: async () => {
        setIsConfirmed(false);
        setIsRejected(false);
        props.refetchReturns(true);
      },
    });
  };

  const headCells = [
    {
      id: 'code',
      numeric: true,
      disablePadding: false,
      label: t('Item code'),
      sortable: false,
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t('Item name'),
      sortable: false,
    },
    {
      id: 'quantity',
      numeric: true,
      disablePadding: false,
      label: t('Quantity'),
      sortable: false,
    },
  ];
  const returnArticles = returnEl?.returnarticles ? t('Yes') : t('No');

  const returnPackage = returnEl?.returnpackage ? t('Yes') : t('No');

  return (
    <div>
      <Button
        onClick={() => {
          handleOpen();
          refetchReturn();
          refetch();
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: 14,
          textTransform: 'none',
        }}
      >
        {props.children}
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <CustomModalBox
          component="form"
          noValidate
          onSubmit={props.handleSubmitPassword}
        >
          <CustomGridContainer
            container
            spacing={2}
          >
            <CustomLeftGrid>
              <ReturnsItemsTable
                rows={rows}
                headCells={headCells}
                statusLoad={itemsStatus}
                setPage={setPage}
                count={count}
                page={page}
              />
            </CustomLeftGrid>
            <CustomRightGrid>
              <Button
                onClick={handleClose}
                sx={{ position: 'absolute', top: -20, right: -25 }}
              >
                <CustomIconBox>
                  <CloseIcon color="secondary" sx={{ fontSize: 25 }} />
                </CustomIconBox>
              </Button>
              { getReturnStatus !== 'loading'
                ? (
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <ImageBox
                      width="70px"
                      height="70px"
                      padding="10px"
                      margin="35px 0 20px"
                      backgroundcolor="#f8f8f8"
                      borderradius={10}
                      objectFit="contain"
                      imgSrc={returnEl?.logo}
                    />
                    <CustomContentContainer>
                      <Typography color="primary" fontWeight={600} fontSize={18} letterSpacing={1}>
                        {returnEl?.customer?.name}
                      </Typography>
                      <CustomTypography color="primary">
                        {returnEl?.customer?.address}
                        ,
                        {returnEl?.customer?.city}
                      </CustomTypography>
                      <CustomTypography color="primary">
                        {t('Tin')}
                        :
                        {returnEl?.customer?.pib}
                      </CustomTypography>
                      <Box className="return-info">
                        <CustomTypography color="primary">
                          {t('Payment type')}
                          :
                          {returnEl?.paymenttype}
                        </CustomTypography>
                        <CustomTypography color="primary">
                          {t('Item return')}
                          :
                          {returnArticles}
                        </CustomTypography>
                        <CustomTypography color="primary">
                          {t('Packaging return')}
                          :
                          {returnPackage}
                        </CustomTypography>
                        {returnEl?.userordernote && (
                        <CustomTypography color="primary">
                          {t('Comment')}
                          :
                          {returnEl?.userordernote}
                        </CustomTypography>
                        )}
                      </Box>
                      <CustomTypography color="primary" marginBottom={2}>
                        {t('Download the order')}
                        :
                      </CustomTypography>
                    </CustomContentContainer>
                  </Box>
                )
                : (
                  <OrderDetailsSkeleton />
                )}
              <CustomContentContainer>
                <Box display="flex" justifyContent="center">
                  <PdfIconButton marginright={15} handleClick={refetchReturnItemsPdf} />
                  <ExcelIconButton handleClick={refetchReturnItemsXls} />
                </Box>
                {props.editReturn
                  && (
                  <Box className="buttons-container" display="flex">
                    <Grid
                      container
                      spacing={2}
                      sx={{ justifyContent: 'center' }}
                    >
                      <Grid item xs={6} md={3} lg={4}>
                        <CommentModal
                          buttontitle={t('Reject')}
                          modaltitle={t('Rejecting Return')}
                          color="#747474"
                          borderradius={16}
                          textsize={10}
                          padding="5px 25px"
                          margin="0"
                          bgcolor="#fff"
                          handleChange={setComment}
                          handleCloseParentModal={handleClose}
                          comment={comment}
                          status="rejected"
                          handleSubmit={handleSubmit}
                          isRejected={isRejected}
                          refetch={props.refetchReturns}
                          confirmedText={t('Return rejected')}
                        />
                      </Grid>
                      <Grid item xs={6} md={3} lg={4}>
                        <CommentModal
                          buttontitle={t('Accept')}
                          modaltitle={t('Accepting Return')}
                          color="#F56F4B"
                          borderradius={16}
                          textsize={10}
                          padding="5px 25px"
                          margin="0"
                          bgcolor="#fff"
                          variant="contained"
                          handleCloseParentModal={handleClose}
                          handleChange={setComment}
                          comment={comment}
                          status="accepted"
                          handleSubmit={handleSubmit}
                          isConfirmed={isConfirmed}
                          refetch={props.refetchReturns}
                          confirmedText={t('Return confirmed')}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  )}
              </CustomContentContainer>
            </CustomRightGrid>
          </CustomGridContainer>
        </CustomModalBox>
      </Modal>
    </div>
  );
}

export default EditReturnModal;
